import React, { useEffect, useMemo, useState } from 'react';
import { Popover } from '@mui/material';
import { useInvoiceSearchMetaDataByColumn } from 'src/use-cases/invoice/useInvoices';
import IconArrowDown from 'src/assets/images/icons/arrow-down.svg';
import { createFilterItems } from '../columnHeader/utils/createFilterItems';
import { createFilterShipmentItems } from './helpers';
import { FilterPopoverContent } from './FilterPopoverContent';
import { TableHeaderMenuProps } from './types';
import { useTableHeaderLogic } from './useTableHeaderLogic';

export const TableHeader: React.FC<TableHeaderMenuProps & { customHeader?: React.ReactNode; showCounter?: boolean; counterValue?: number }> = ({
  column,
  onSort,
  onFilter,
  sortAscText = 'Sort A to Z',
  sortDescText = 'Sort Z to A',
  filterValues: filterItemsProp = [],
  initialSelectedValues = [],
  showCounter,
  counterValue,
  onOpen,
  onLoadMore,
  onSearchValueChange,
  icon,
  customHeader,
  isLoading,
  isLoadingMore,
  hasMore,
  // NEW: Lazy loading props
  columnKey,
  filters,
  keyword,
}) => {
  // Track if filter popover has been opened (for lazy loading)
  const [hasOpenedFilter, setHasOpenedFilter] = useState(false);

  // LocalStorage key for this column's SPOT filter state
  const spotFilterStorageKey = `spot-filter-${column.id}`;

  // Track if user applied filter with "Only Show SPOT" enabled
  // Always start as FALSE - do not restore from localStorage on mount
  // This ensures the toggle is always OFF by default (showing all shipments)
  const [wasSpotFilterApplied, setWasSpotFilterApplied] = useState(false);

  // Track APPLIED filter values (what user confirmed with Apply button)
  // This is separate from selectedValues in the popover (which changes in real-time)
  const [appliedFilterValues, setAppliedFilterValues] = useState<string[]>(initialSelectedValues);

  // Lazy load metadata only when filter opens (if columnKey is provided)
  const { data: columnMetadata, isLoading: isLoadingMetadata } = useInvoiceSearchMetaDataByColumn(
    columnKey || '',
    keyword || '',
    filters || {},
    hasOpenedFilter && !!columnKey // Only fetch when opened and columnKey exists
  );

  // Transform API response to filter items (lazy loaded)
  const lazyFilterItems = useMemo(() => {
    // Use shipment-specific helper for shipmentNumber column (uses detailedCounts for spot shipment info)
    if (columnKey === 'SHIPMENT_NUMBER') {
      if (!columnMetadata?.detailedCounts) return [];
      return createFilterShipmentItems(columnMetadata.detailedCounts);
    }
    // For other columns, use simple counts
    if (!columnMetadata?.counts) return [];
    return createFilterItems(columnMetadata.counts, column.id);
  }, [columnMetadata, columnKey]);

  // Use lazy loaded items if available, otherwise fall back to prop
  const filterItems = columnKey ? lazyFilterItems : filterItemsProp;
  const isFilterLoading = isLoading ?? (columnKey ? isLoadingMetadata : false);
  // Get current sort state from column
  const currentSortDirection = column.getIsSorted() as string | false;

  // Override initialSelectedValues if SPOT filter was applied with specific selections
  const effectiveInitialSelectedValues = useMemo(() => {
    if (wasSpotFilterApplied && columnKey === 'SHIPMENT_NUMBER') {
      const saved = localStorage.getItem(spotFilterStorageKey);
      if (saved) {
        try {
          const { selectedIds } = JSON.parse(saved);
          return selectedIds || initialSelectedValues;
        } catch {
          return initialSelectedValues;
        }
      }
    }
    return initialSelectedValues;
  }, [wasSpotFilterApplied, columnKey, spotFilterStorageKey, initialSelectedValues]);

  // Determine if we should use auto-select behavior
  // If counter props are provided externally, don't auto-select (parent controls state)
  const useAutoSelectAll = showCounter === undefined && counterValue === undefined;

  // Use shared hook for all filter/selection logic
  const {
    anchorEl,
    open,
    searchTerm,
    selectedValues,
    filteredItems,
    onlyShowSpot,
    setSearchTerm,
    setSelectedValues,
    handleHeaderClick,
    handleClose,
    handleSelectAll,
    handleClearAll,
    handleToggleValue,
    setOnlyShowSpot,
  } = useTableHeaderLogic({
    filterItems,
    initialSelectedValues: effectiveInitialSelectedValues, // Use saved selections if SPOT filter was applied
    useAutoSelectAll,
    onOpen,
    initialSpotFilter: wasSpotFilterApplied, // Restore toggle state if SPOT filter was applied
  });

  useEffect(() => {
    onSearchValueChange?.(searchTerm);
  }, [onSearchValueChange, searchTerm]);

  const handleHeaderClickWithLazyLoad = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Mark filter as opened for lazy loading
    if (columnKey && !hasOpenedFilter) {
      setHasOpenedFilter(true);
    }
    // Restore toggle state when reopening
    if (wasSpotFilterApplied && columnKey === 'SHIPMENT_NUMBER') {
      setOnlyShowSpot(true);
    }
    handleHeaderClick(event);
    if (onOpen) {
      onOpen();
    }
  };

  const handleApplySort = (direction: 'asc' | 'desc') => {
    if (onSort) {
      onSort(column.id, direction);
    }
    handleClose();
  };

  const handleApplyFilter = () => {
    if (onFilter) {
      // If spot filter is active, only apply values that are currently visible (SPOT items only)
      let valuesToApply = selectedValues;

      if (onlyShowSpot && columnKey === 'SHIPMENT_NUMBER') {
        // Filter selected values to only include items that have hasSpotShipment = true
        const spotItemIds = new Set(filterItems.filter((item: any) => item.hasSpotShipment).map((item: any) => item.id));
        valuesToApply = selectedValues.filter((value) => spotItemIds.has(value));

        // Update selectedValues to only contain SPOT items
        // This ensures the counter shows the correct number (only SPOT items)
        setSelectedValues(valuesToApply);

        // Save to localStorage: SPOT filter is enabled with these specific values
        localStorage.setItem(
          spotFilterStorageKey,
          JSON.stringify({
            enabled: true,
            selectedIds: valuesToApply,
          })
        );
        setWasSpotFilterApplied(true);
      } else {
        // Clear localStorage if not using spot filter
        localStorage.removeItem(spotFilterStorageKey);
        setWasSpotFilterApplied(false);
      }

      // Update applied filter values (for counter display)
      setAppliedFilterValues(valuesToApply);

      onFilter(column.id, valuesToApply);
    }
    handleClose();
  };

  // Calculate counter based on APPLIED filter values, not current selection in popover
  const appliedCounter = useMemo(() => {
    const availableItemIds = filterItems.map((item) => item.id);
    const appliedAvailableCount = appliedFilterValues.filter((value) => availableItemIds.includes(value)).length;

    // Show counter if:
    // 1. Some items are applied (> 0)
    // 2. AND either:
    //    a) Not all items are applied (< total), OR
    //    b) SPOT filter was applied (wasSpotFilterApplied is true)
    const shouldShowCounter = appliedAvailableCount > 0 && (appliedAvailableCount < availableItemIds.length || wasSpotFilterApplied);
    return { shouldShowCounter, appliedAvailableCount };
  }, [appliedFilterValues, filterItems, wasSpotFilterApplied]);

  // Use provided counter props if available, otherwise use applied counter
  const finalShowCounter = showCounter ?? appliedCounter.shouldShowCounter;
  const finalCounterValue = counterValue ?? appliedCounter.appliedAvailableCount;

  return (
    <>
      <div className=' flex items-center w-full gap-8' style={{ height: '100%', minHeight: 24, display: 'flex', alignItems: 'center' }}>
        {icon && <div className='flex items-center justify-center w-16 h-16 flex-shrink-0'>{icon}</div>}
        {!customHeader && column.columnDef.header && <span className='text-left flex-1 mr-5'>{column.columnDef.header}</span>}
        {customHeader && <div>{customHeader}</div>}
        <div className='ml-auto flex items-center'>
          {finalShowCounter && (
            <div className='ml-12 rounded-full px-6 border border-primary text-primary text-[10px] leading-[18px] font-bold'>{finalCounterValue}</div>
          )}
          <button type='button' onClick={handleHeaderClickWithLazyLoad} className='w-16 ml-8'>
            <img src={IconArrowDown} alt='Sort' className='w-full' />
          </button>
        </div>
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPaper-root': {
            width: 345,
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
          },
        }}
      >
        <FilterPopoverContent
          currentSortDirection={currentSortDirection}
          sortAscText={sortAscText}
          sortDescText={sortDescText}
          onApplySort={handleApplySort}
          filterItems={filterItems}
          filteredItems={filteredItems}
          selectedValues={selectedValues}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
          onToggleValue={handleToggleValue}
          onApplyFilter={handleApplyFilter}
          isLoading={isFilterLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          showFilter={!!onFilter}
          showSpotFilter={columnKey === 'SHIPMENT_NUMBER'}
          onlyShowSpot={onlyShowSpot}
          onToggleSpotFilter={setOnlyShowSpot}
        />
      </Popover>
    </>
  );
};
